import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, FilePlus2, Search, Download } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RiskBadge } from "@/components/risk/RiskBadge";
import { EmptyState } from "@/components/common/EmptyState";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import api, { getErrorMessage, downloadReportPdf } from "@/lib/api";
import { PLATFORMS } from "@/lib/risk";
import { useAuth } from "@/context/AuthContext";

export default function History() {
  const { user } = useAuth();
  const isPaid = user?.plan === "pro" || user?.plan === "pm";
  const [items, setItems] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [riskBand, setRiskBand] = useState("All");
  const [platform, setPlatform] = useState("All");
  const [propertyId, setPropertyId] = useState("All");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const downloadPdf = async (id, initials) => {
    setDownloading(id);
    try {
      await downloadReportPdf(id, `hostguard-evidence-${(initials || "report").replace(/[^a-zA-Z0-9]/g, "")}.pdf`);
      toast.success("Evidence report downloaded");
    } catch (e) {
      toast.error(getErrorMessage(e, "Could not generate the PDF."));
    } finally {
      setDownloading(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (riskBand !== "All") params.risk_band = riskBand;
    if (platform !== "All") params.platform = platform;
    if (propertyId !== "All") params.property_id = propertyId;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    try {
      const res = await api.get("/assessments", { params });
      setItems(res.data);
    } finally {
      setLoading(false);
    }
  }, [riskBand, platform, propertyId, dateFrom, dateTo]);

  useEffect(() => { api.get("/properties").then((r) => setProperties(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const action = <Link to="/app/new"><Button className="gap-2" data-testid="history-new-button"><FilePlus2 className="h-4 w-4" /> New assessment</Button></Link>;

  return (
    <AppShell title="Assessment History" action={action}>
      <div className="space-y-5">
        {/* Filters */}
        <div className="rounded-xl border bg-card p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Risk level</label>
              <Select value={riskBand} onValueChange={setRiskBand}>
                <SelectTrigger data-testid="filter-risk-band"><SelectValue /></SelectTrigger>
                <SelectContent>{["All", "Low", "Medium", "High"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Platform</label>
              <Select value={platform} onValueChange={setPlatform}>
                <SelectTrigger data-testid="filter-platform"><SelectValue /></SelectTrigger>
                <SelectContent>{["All", ...PLATFORMS].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Property</label>
              <Select value={propertyId} onValueChange={setPropertyId}>
                <SelectTrigger data-testid="filter-property"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All</SelectItem>
                  {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">From</label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">To</label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex h-48 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : items.length === 0 ? (
          <EmptyState icon={Search} title="No assessments found" description="Try adjusting filters, or create your first assessment."
            action={<Link to="/app/new"><Button className="gap-2"><FilePlus2 className="h-4 w-4" /> New assessment</Button></Link>} />
        ) : (
          <div className="overflow-hidden rounded-xl border bg-card">
            <Table data-testid="assessment-history-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Guest</TableHead>
                  <TableHead>Platform</TableHead>
                  <TableHead>Property</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Score</TableHead>
                  <TableHead>Risk</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((a) => (
                  <TableRow key={a.id} className="hover:bg-accent/40" data-testid={`history-row-${a.id}`}>
                    <TableCell className="font-medium">{a.guest_initials || "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{a.platform}</TableCell>
                    <TableCell className="text-muted-foreground">{a.property_name || "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{new Date(a.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right font-display font-semibold tabular-nums">{a.overall_score}</TableCell>
                    <TableCell><RiskBadge band={a.risk_band} size="sm" /></TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {isPaid && (
                          <Button variant="ghost" size="icon" title="Download PDF" disabled={downloading === a.id} onClick={() => downloadPdf(a.id, a.guest_initials)} data-testid={`history-pdf-${a.id}`}>
                            {downloading === a.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                          </Button>
                        )}
                        <Link to={`/app/result/${a.id}`}><Button variant="ghost" size="sm" data-testid={`history-view-${a.id}`}>View</Button></Link>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
